import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import mammoth from "mammoth";
import Pagination from "react-js-pagination";

import Utils from "../../../../utils"; 
import useDocumentCounts from "./useDocumentCounts";
import useDocuments from "./hooks/useDocuments";

// Components
import FilterBar from "./components/FilterBar";
import DocumentTable from "./components/DocumentTable";
import TagModal from "./components/Modals/TagModal";
import DocumentPreviewModal from "./components/Modals/DocumentPreviewModal";
import DocumentFormOffcanvas from "./components/Offcanvas/DocumentFormOffcanvas";
import DocumentDetailsOffcanvas from "./components/Offcanvas/DocumentDetailsOffcanvas";
import SleeveFilesSummaryGenerator from "./ViewSleevesFiles/SleeveFilesSummaryGenerator";

import { getEnvelopeSenderList, getManageEnvelopeList, sleeveFilePreviewDetailsForm, sleeveFileDetailsForm } from "../../../../services/CommonService";
import { commonFields, documentFields } from "../../../../components/fields";

import './envelopeTabs.css';

const EnvFiles = () => {
  const navigate = useNavigate();
  
  // State
  const [dataParams, setDataParams] = useState({ limit: 10, page: 1 });
  const [senderList, setSenderList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userId, setUserId] = useState(null);
  
  // UI State
  const [searchText, setSearchText] = useState("");
  const [tags, setTags] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedOption, setSelectedOption] = useState('Contract Files');
  
  // Modals & Offcanvas State
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [tagInputs, setTagInputs] = useState({});
  
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  
  const [formOffcanvasOpen, setFormOffcanvasOpen] = useState(false);
  const [detailsOffcanvasOpen, setDetailsOffcanvasOpen] = useState(false);
  const [formDetails, setFormDetails] = useState({});
  const [formData, setFormData] = useState({});
  const [formLoading, setFormLoading] = useState(false);

  // Custom Hooks
  const {
    counts: { totalInProgress, totalDraft, totalCompleted },
    setTotalInProgress,
    setTotalDraft,
    setTotalCompleted,
    loading: countsLoading,
    setLoading: setCountsLoading,
  } = useDocumentCounts({
    userEmail: Utils.loginUserData()?.email,
    selectedUserId,
    dataParams,
    userId,
  });

  const {
    fetchedDocuments,
    totalDocuments,
    loading: docsLoading,
    fetchDocuments,
    saveKeyword,
    removeKeyword: deleteKeywordHook,
    errorMessages,
    setErrorMessages,
    setLoading: setDocsLoading
  } = useDocuments(selectedUserId, dataParams);

  const loading = countsLoading || docsLoading || formLoading;
  const setLoading = (val) => {
    setCountsLoading(val);
    setDocsLoading(val);
  };

  useEffect(() => {
    const userData = Utils.loginUserData();
    if (userData && userData.id) setUserId(userData.id);

    getEnvelopeSenderList()
      .then((response) => {
        const senderData = response.data.data;
        setSenderList(senderData);
        if (senderData.length > 0 && senderData[0].id) {
          setSelectedUserId(Number(senderData[0].id));
        }
      })
      .catch(() => toast.error("Failed to fetch sender list!"));
  }, []);

  useEffect(() => {
    getManageEnvelopeList(dataParams)
      .then((response) => {
        if (response.data.status_count.length > 0) {
          setTotalInProgress(parseInt(response.data.status_count[0] || 0));
          setTotalDraft(parseInt(response.data.status_count[1] || 0));
          setTotalCompleted(parseInt(response.data.status_count[2] || 0));
        }
      })
      .catch(() => {});
  }, [dataParams, userId, setTotalInProgress, setTotalDraft, setTotalCompleted]);

  useEffect(() => {
    if (selectedUserId) {
      fetchDocuments(searchText, dataParams.page, dataParams.limit);
    }
  }, [selectedUserId, dataParams.limit, fetchDocuments, searchText, dataParams.page]);

  const handleData = (e, type) => {
    let params = { ...dataParams };
    if (type === "pagination") {
      params.page = e;
    } else if (type === "limit") {
      params.limit = parseInt(e.target.value);
      params.page = 1;
    }
    setDataParams(params);
  };

  const handleDropdownOptionClick = (option) => {
    setSelectedOption(option.label);
    navigate(option.route);
  };

  const dropdownOptions = [
    { label: "In Progress", color: "partial", badge: totalInProgress, route: "/manage" },
    { label: "Draft", color: "unpaid", badge: totalDraft, route: "/manage/draft" },
    { label: "Completed", color: "paid", badge: totalCompleted, route: "/manage/completed" },
    { label: "Contract Files", color: "files", route: "/manage/files" },
  ];

  // Document Preview
  const handleOpenDocumentModal = async (doc) => {
    if (!doc || !doc.file_path) return;

    try {
      setLoading(true);
      const fileNameLower = doc.file_name.toLowerCase();

      if (fileNameLower.endsWith(".docx")) {
        const response = await fetch(doc.file_path);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setPreviewContent(result.value);
      } else {
        setPreviewContent("");
      }
      
      setPreviewDoc(doc);
      setPreviewModalOpen(true);
    } catch (error) {
      console.error("Error loading document:", error);
      toast.error("Error loading document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Summary
  const handleOpenSummaryModal = async (doc) => {
    if (!doc || !doc.file_path) {
      toast.error("No file path available.");
      return;
    }
    try {
      setLoading(true);
      setSelectedDoc(doc);
      setSummaryModalOpen(true);
      
      localStorage.setItem('currentDocument', JSON.stringify(doc));
      const randomId = Math.floor(Math.random() * 1000000);
      navigate(`/manage/files/summary/${randomId}`, { replace: true });

      if (doc.file_name.toLowerCase().endsWith(".docx")) {
        const response = await fetch(doc.file_path);
        const arrayBuffer = await response.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        setPreviewContent(result.value); 
      } else {
        setPreviewContent("");
      }
    } catch (error) {
      setPreviewContent("");
      toast.error(`Error preparing summary: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSummaryModal = () => {
    setSummaryModalOpen(false);
    setSelectedDoc(null);
    localStorage.removeItem('currentDocument');
    navigate("/manage/files", { replace: true });
  };

  // Tags
  const handleOpenTagModal = (doc) => {
    if (doc && doc.file_path) {
      setSelectedDoc(doc);
      setTagInputs({ [doc.file_path]: "" });
      setTagModalOpen(true);
    } else {
      toast.error("Invalid document data.");
    }
  };

  const onSaveKeyword = async (e, filePath) => {
      if (e.key === "Enter") {
          const success = await saveKeyword(selectedDoc, tagInputs[filePath], selectedDoc.envelope_id, searchText);
          if (success) {
            setTagInputs(prev => ({...prev, [filePath]: ""}));
          }
      }
  }

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter" && searchText.trim()) {
      const trimmedKeyword = searchText.trim();
      if (!tags.includes(trimmedKeyword)) {
        const newTags = [...tags, trimmedKeyword];
        setTags(newTags);
        setDataParams((prev) => ({ ...prev, page: 1 }));
        fetchDocuments(newTags.join(","), 1, dataParams.limit);
      }
      setSearchText("");
    }
  };

  // Details & Form
  const handleOpenFormOffcanvas = async (doc) => {
    if (!doc || !doc.envelope_id) return toast.error("Invalid document.");
    
    setSelectedDoc(doc);
    setFormLoading(true);
    setFormOffcanvasOpen(true);
    
    try {
        const payload = { user_id: Number(selectedUserId), envelope_id: Number(doc.envelope_id), file_name: doc.file_name };
        const response = await sleeveFilePreviewDetailsForm(payload);
        const details = response?.data?.formDetails || {};
        setFormData(Object.keys(details).length > 0 ? { ...details, additional_data: details.additional_data || {} } : { document_type: doc.document_type || "" });
    } catch (error) {
        setFormData({ document_type: doc.document_type || "" });
    } finally {
        setFormLoading(false);
    }
  };

  const handleOpenDetailsOffcanvas = async (doc) => {
    if (!doc || !doc.envelope_id) return toast.error("Invalid document.");
    
    setSelectedDoc(doc);
    setFormLoading(true);
    setDetailsOffcanvasOpen(true);

    try {
        const payload = { user_id: Number(selectedUserId), envelope_id: Number(doc.envelope_id), file_name: doc.file_name };
        const response = await sleeveFilePreviewDetailsForm(payload);
        const details = response?.data?.formDetails || {};
        setFormDetails(Object.keys(details).length > 0 ? details : { 
            user_id: Number(selectedUserId), 
            envelope_id: Number(doc.envelope_id), 
            file_name: doc.file_name, 
            document_type: doc.document_type || "", 
            additional_data: {} 
        });
    } catch (error) {
        setFormDetails({});
    } finally {
        setFormLoading(false);
    }
  };

  const handleSubmitForm = async (submittedData) => {
    if (!selectedDoc || !selectedDoc.envelope_id) return toast.error("No valid document.");

    try {
      setFormLoading(true);
      const cleanedData = {
        document_type: submittedData.document_type === "N/A" ? null : submittedData.document_type,
        document_number: submittedData.document_number || "",
        owner_name: submittedData.owner_name || "",
        remarks: submittedData.remarks || "",
        address: submittedData.address || "",
        additional_data: submittedData.additional_data || {},
      };

      const payload = {
        user_id: Number(selectedUserId),
        envelope_id: Number(selectedDoc.envelope_id),
        file_name: selectedDoc.file_name,
        ...cleanedData,
        ...(selectedDoc.hasDetails && { _method: "PUT" })
      };

      await sleeveFileDetailsForm(payload);

      fetchDocuments(searchText, dataParams.page, dataParams.limit);
      
      setFormDetails(payload);
      toast.success("Details saved!");
      setFormOffcanvasOpen(false);
      setDetailsOffcanvasOpen(true);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setFormLoading(false);
    }
  };
  
  return (
    <>
      {loading && (
        <div className="page-loading">
          <img src="/images/loader.gif" alt="loader" />
        </div>
      )}
      <section className="main_wrapper section-bg-color h-100">
        <div className="custom_container">
          <FilterBar
            searchText={searchText}
            setSearchText={setSearchText}
            tags={tags}
            handleKeyDown={handleSearchKeyDown}
            handleManageSleeve={() => navigate("/manage/files")}
            handleManageNegotiation={() => navigate("/manage/negotiation")}
            createContract={() => navigate("/client-portal/sleeve")}
            selectedOption={selectedOption}
            dropdownOptions={dropdownOptions}
            handleDropdownOptionClick={handleDropdownOptionClick}
            activeTab="contract"
          />

          <div id="nav-tabContent">
            <DocumentTable
              paginatedDocuments={fetchedDocuments} 
              searchText={searchText}
              tags={tags}
              expandedRows={expandedRows}
              toggleExpand={(idx) => setExpandedRows(prev => ({ ...prev, [idx]: !prev[idx] }))}
              removeKeyword={(doc, k) => deleteKeywordHook(doc, k, searchText)}
              handleOpenDocumentModal={handleOpenDocumentModal}
              handleOpenModal={handleOpenTagModal}
              handleOpenSummaryModal={handleOpenSummaryModal}
              handleOpenFormOffcanvas={handleOpenFormOffcanvas}
              handleOpenDetailsOffcanvas={handleOpenDetailsOffcanvas}
            />
          </div>

          {totalDocuments > dataParams.limit && (
             <div className="table_footer_wrap">
                <div className="d-flex align-items-center">
                    <span className="me-2 text-white fw-bolder fs-6">Show</span>
                    <select
                    className="form-select"
                    value={dataParams.limit}
                    onChange={(e) => handleData(e, "limit")}
                    style={{ width: "80px" }}
                    >
                    {Utils.tableShowLengths().map((item, i) => (
                        <option value={item} key={i}>{item}</option>
                    ))}
                    </select>
                </div>
                <div className="pagination mt-3">
                  <Pagination
                    activePage={dataParams.page}
                    itemsCountPerPage={dataParams.limit}
                    totalItemsCount={totalDocuments}
                    pageRangeDisplayed={5}
                    onChange={(e) => handleData(e, "pagination")}
                  />
                </div>
             </div>
          )}
        </div>

        <DocumentFormOffcanvas
            isOpen={formOffcanvasOpen}
            handleClose={() => setFormOffcanvasOpen(false)}
            selectedDoc={selectedDoc}
            formData={formData}
            setFormData={setFormData}
            commonFields={commonFields}
            documentFields={documentFields}
            handleSubmit={handleSubmitForm}
        />

        <DocumentDetailsOffcanvas
            isOpen={detailsOffcanvasOpen}
            handleClose={() => setDetailsOffcanvasOpen(false)}
            selectedDoc={selectedDoc}
            formDetails={formDetails}
            loading={formLoading}
            commonFields={commonFields}
            handleEditDetails={(doc, details) => {
                setFormData({ ...details, additional_data: details.additional_data || {} });
                setDetailsOffcanvasOpen(false);
                setFormOffcanvasOpen(true);
            }}
        />
      </section>

      <DocumentPreviewModal
        isOpen={previewModalOpen}
        handleClose={() => { setPreviewModalOpen(false); setPreviewDoc(null); }}
        selectedDoc={previewDoc}
        content={previewContent}
      />

      <TagModal
        isOpen={tagModalOpen}
        handleClose={() => { setTagModalOpen(false); setSelectedDoc(null); }}
        selectedDoc={selectedDoc}
        tagInputs={tagInputs}
        setTagInputs={setTagInputs}
        handleKeyPress={onSaveKeyword}
        errorMessages={errorMessages}
        removeKeyword={(doc, k) => deleteKeywordHook(doc, k, searchText)}
      />

      {summaryModalOpen && (
        <SleeveFilesSummaryGenerator
          selectedDoc={selectedDoc}
          onClose={handleCloseSummaryModal}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default EnvFiles;
